import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchLoyaltyPrograms,
  resequenceLoyaltyPrograms,
  updateLoyaltyProgramStatus,
} from "../../../apiService/loyalty.service";
import { getApiErrorMessage, getProgramId, mapProgramRow, nextProgramStatus } from "../utils";

export const useProgramsList = (productId) => {
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [togglingIds, setTogglingIds] = useState([]);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const programsRef = useRef([]);
  const savedOrderIdsRef = useRef("");

  useEffect(() => {
    let cancelled = false;

    const loadPrograms = async () => {
      setIsLoading(true);
      setError("");
      try {
        const list = await fetchLoyaltyPrograms(productId);
        if (cancelled) return;
        const mapped = (Array.isArray(list) ? list : []).map(mapProgramRow);
        setPrograms(mapped);
        savedOrderIdsRef.current = mapped.map((item) => item.id).join(",");
      } catch (loadError) {
        if (!cancelled) {
          setPrograms([]);
          setError(getApiErrorMessage(loadError, "Failed to load loyalty programs."));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPrograms();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  programsRef.current = programs;

  const handleSetProgramList = useCallback((newList) => {
    programsRef.current = newList;
    setPrograms(newList);
  }, []);

  const handleToggleStatus = useCallback(async (program) => {
    const programId = getProgramId(program);
    if (!programId) return;

    let alreadyToggling = false;
    setTogglingIds((prev) => {
      if (prev.includes(programId)) {
        alreadyToggling = true;
        return prev;
      }
      return [...prev, programId];
    });
    if (alreadyToggling) return;

    setActionError("");
    try {
      await updateLoyaltyProgramStatus(programId);
      setPrograms((prev) =>
        prev.map((item) =>
          getProgramId(item) === programId
            ? { ...item, status: nextProgramStatus(item.status) }
            : item
        )
      );
    } catch (statusError) {
      setActionError(getApiErrorMessage(statusError, "Failed to update loyalty program status."));
    } finally {
      setTogglingIds((prev) => prev.filter((id) => id !== programId));
    }
  }, []);

  const saveProgramOrder = useCallback(async () => {
    const ids = programsRef.current.map((item) => getProgramId(item)).filter(Boolean);
    const nextOrder = ids.join(",");
    if (!ids.length || nextOrder === savedOrderIdsRef.current) return;
    if (!productId) {
      setActionError("Select a product in the header before reordering programs.");
      return;
    }

    setIsSavingOrder(true);
    setActionError("");
    try {
      await resequenceLoyaltyPrograms(productId, ids);
      savedOrderIdsRef.current = nextOrder;
    } catch (saveError) {
      setActionError(getApiErrorMessage(saveError, "Failed to save program order."));
    } finally {
      setIsSavingOrder(false);
    }
  }, [productId]);

  return {
    programs,
    isLoading,
    error,
    actionError,
    isSavingOrder,
    togglingIds,
    handleSetProgramList,
    handleToggleStatus,
    saveProgramOrder,
  };
};
